import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { ABOUT_IMAGE, SITE } from "@/config/site";

export default function About() {
  return (
    <>
      <Seo
        title="About DJC Global — A Connection With Context"
        description="DJC Global connects businesses with specialized solution providers — across savings, benefits, healthcare operations, marketing, and emerging opportunities."
        path="/about"
      />
      <PageHero
        eyebrow="About DJC Global"
        title="We know where the doors are."
        sub="DJC Global connects businesses with specialized solutions that can improve operations, evaluate savings opportunities, strengthen employee benefits, grow through marketing, and explore what comes next."
      >
        <img
          src={ABOUT_IMAGE}
          alt="Modern glass towers reaching into a bright sky"
          loading="lazy"
          className="mt-10 h-[260px] w-full rounded-md border border-silver/60 object-cover sm:h-[380px]"
          data-testid="about-hero-image"
        />
      </PageHero>

      <section className="bg-platinum pb-16 pt-4 sm:pb-24" data-testid="about-body">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
            <Reveal className="lg:col-span-7">
              <h2 className="font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                What DJC Global is
              </h2>
              <div className="mt-5 space-y-4 text-lg leading-relaxed text-ink/75">
                <p>
                  DJC Global is a connection point between businesses and specialized providers.
                  Behind each of our seven solution destinations is an independent company with its
                  own expertise, delivery team, and terms.
                </p>
                <p className="text-base text-ink/70">
                  Our value is context. We understand what each provider actually does, who it fits,
                  and what it might help improve — so a business with a specific challenge can skip
                  the search and start the right conversation.
                </p>
              </div>
              <h2 className="mt-12 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                What we are — and aren't
              </h2>
              <div className="mt-5 grid gap-5 sm:grid-cols-2">
                <div className="rounded-md border-2 border-cobalt/70 bg-white p-6" data-testid="about-are-card">
                  <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cobalt">
                    We are
                  </p>
                  <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ink/75">
                    <li>— A guide to seven vetted solution destinations</li>
                    <li>— A first conversation that narrows the field</li>
                    <li>— A connection to the specialist who delivers</li>
                    <li>— Straight about referral compensation</li>
                  </ul>
                </div>
                <div className="rounded-md border border-silver/60 bg-white p-6" data-testid="about-arent-card">
                  <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-ink/60">
                    We are not
                  </p>
                  <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ink/75">
                    <li>— The delivery team for any provider's services</li>
                    <li>— A source of tax, legal, or investment advice</li>
                    <li>— A guarantor of savings, refunds, or results</li>
                    <li>— A gate between you and the providers</li>
                  </ul>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08} className="lg:col-span-5">
              <div className="rounded-md bg-midnight p-7 text-white lg:p-8" data-testid="about-contact-card">
                <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-silver">
                  Talk with DJC Global
                </p>
                <p className="mt-4 text-lg leading-relaxed text-white/80">
                  Questions about where to start? A short call usually points to one or two relevant
                  destinations.
                </p>
                <div className="mt-6 space-y-3">
                  <a
                    href={SITE.phoneHref}
                    data-testid="about-phone-link"
                    className="flex items-center gap-3 rounded-md border border-white/25 px-4 py-3 font-display text-sm font-semibold transition-colors hover:bg-white/10"
                  >
                    <Phone className="h-4 w-4 text-silver" /> {SITE.phone}
                  </a>
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="about-email-link"
                    className="flex items-center gap-3 rounded-md border border-white/25 px-4 py-3 font-display text-sm font-semibold transition-colors hover:bg-white/10"
                  >
                    <Mail className="h-4 w-4 text-silver" /> {SITE.email}
                  </a>
                </div>
                <Link
                  to="/contact"
                  data-testid="about-contact-cta"
                  className="group mt-6 inline-flex items-center gap-2 rounded-md bg-white px-6 py-3 font-display text-sm font-semibold text-midnight transition-colors hover:bg-silver"
                >
                  Send an Inquiry
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <p className="mt-6 border-t border-white/20 pt-4 text-xs leading-relaxed text-white/55">
                  DJC Global may receive compensation from certain referrals.{" "}
                  <Link to="/referral-disclosure" className="underline hover:text-white">
                    Referral disclosure
                  </Link>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
